import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Clock, Truck, Package, CheckCircle2, XCircle, ArrowLeft, MapPin, Copy, FileText, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const pdfUrl = (id) => `${API_BASE}/api/coas/${id}/pdf`;

const FLOW = [
    { key: "order_placed", label: "Order Placed", icon: Clock },
    { key: "processing", label: "Processing", icon: Package },
    { key: "shipped", label: "Shipped", icon: Truck },
    { key: "out_for_delivery", label: "Out for Delivery", icon: Truck },
    { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const Timeline = ({ status, history, canceled }) => {
    if (canceled) {
        return (
            <div className="flex items-center gap-3 p-5 rounded-2xl border border-red-500/30 bg-red-500/5" data-testid="order-canceled-banner">
                <XCircle className="text-red-300" />
                <div>
                    <div className="text-red-200 font-display font-bold">Order Canceled</div>
                    <div className="text-xs text-red-200/60">If unexpected, contact support &mdash; we&apos;ll sort it out.</div>
                </div>
            </div>
        );
    }
    const currentIdx = FLOW.findIndex((s) => s.key === status);
    const activeIdx = currentIdx === -1 ? 0 : currentIdx;

    return (
        <div className="relative" data-testid="order-timeline">
            {/* Desktop horizontal timeline */}
            <div className="hidden md:flex items-start justify-between relative">
                <div className="absolute top-5 left-5 right-5 h-[2px] bg-white/10" />
                <div
                    className="absolute top-5 left-5 h-[2px] bg-gradient-to-r from-[var(--gold)] to-[#F5C932] transition-all duration-700"
                    style={{ width: `calc((100% - 2.5rem) * ${activeIdx} / ${FLOW.length - 1})` }}
                />
                {FLOW.map((step, idx) => {
                    const reached = idx <= activeIdx;
                    const current = idx === activeIdx;
                    const Icon = step.icon;
                    const histEntry = history?.find((h) => h.status === step.key);
                    return (
                        <div key={step.key} className="relative flex flex-col items-center w-1/5 text-center" data-testid={`stage-${step.key}`}>
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 z-10 transition-all duration-300 ${
                                    reached
                                        ? "bg-[var(--gold)] border-[var(--gold)] text-black"
                                        : "bg-black border-white/15 text-white/30"
                                } ${current ? "ring-4 ring-[var(--gold)]/30" : ""}`}
                            >
                                {reached ? <Check size={16} strokeWidth={3} /> : <Icon size={14} />}
                            </div>
                            <div className={`mt-3 text-[11px] font-mono uppercase tracking-widest ${reached ? "text-white" : "text-white/30"}`}>
                                {step.label}
                            </div>
                            {histEntry?.changed_at && (
                                <div className="text-[10px] text-[var(--text-muted)] mt-1">
                                    {new Date(histEntry.changed_at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Mobile vertical timeline */}
            <div className="md:hidden space-y-3">
                {FLOW.map((step, idx) => {
                    const reached = idx <= activeIdx;
                    const current = idx === activeIdx;
                    const Icon = step.icon;
                    const histEntry = history?.find((h) => h.status === step.key);
                    return (
                        <div key={step.key} className="flex items-start gap-3" data-testid={`stage-mobile-${step.key}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${
                                        reached
                                            ? "bg-[var(--gold)] border-[var(--gold)] text-black"
                                            : "bg-black border-white/15 text-white/30"
                                    } ${current ? "ring-4 ring-[var(--gold)]/30" : ""}`}
                                >
                                    {reached ? <Check size={14} strokeWidth={3} /> : <Icon size={12} />}
                                </div>
                                {idx < FLOW.length - 1 && (
                                    <div className={`w-[2px] h-8 ${idx < activeIdx ? "bg-[var(--gold)]" : "bg-white/10"}`} />
                                )}
                            </div>
                            <div className="pb-4 flex-1">
                                <div className={`text-sm font-mono uppercase tracking-wider ${reached ? "text-white" : "text-white/30"}`}>
                                    {step.label}
                                </div>
                                {histEntry?.changed_at && (
                                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                                        {new Date(histEntry.changed_at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const TrackOrderPage = () => {
    const { reference } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/orders/${reference}`);
                setOrder(data);
            } catch (err) {
                setError(err.response?.status === 404 ? "Order not found" : "Could not load this order");
            }
            setLoading(false);
        })();
    }, [reference]);

    const copyRef = () => {
        navigator.clipboard.writeText(reference);
        toast.success("Reference copied");
    };

    if (loading) {
        return (
            <div className="pt-28 pb-20 min-h-screen flex items-center justify-center">
                <div className="inline-block w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="pt-28 pb-20 min-h-screen relative" data-testid="track-order-error">
                <MolecularBg intensity="low" />
                <div className="container-x relative z-10 max-w-2xl text-center">
                    <h1 className="font-display text-3xl text-white mb-2">{error || "Order not found"}</h1>
                    <p className="text-[var(--text-secondary)] mb-6">Check your reference or contact support.</p>
                    <Link to="/" className="btn-solid inline-block">Back to home</Link>
                </div>
            </div>
        );
    }

    const a = order.shipping_address || {};
    const fulfillment = order.fulfillment_status || (order.payment_status === "paid" ? "order_placed" : null);
    const canceled = fulfillment === "canceled" || order.payment_status === "canceled";

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="track-order-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10 max-w-3xl">
                <Link to="/account" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--gold)] mb-6">
                    <ArrowLeft size={14} /> Back to account
                </Link>

                <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
                    <div>
                        <span className="eyebrow">// Order Tracking</span>
                        <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 italic">
                            Order <span className="text-[var(--gold)]">{order.reference}</span>
                        </h1>
                        <button
                            onClick={copyRef}
                            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--gold)]"
                        >
                            <Copy size={11} /> Copy reference
                        </button>
                    </div>
                    <div className="text-right">
                        <div className="text-3xl font-display font-extrabold text-white">A${Number(order.amount || 0).toFixed(2)}</div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">{order.currency?.toUpperCase() || "AUD"}</div>
                    </div>
                </div>

                {!fulfillment && order.payment_status !== "paid" && (
                    <div className="glass rounded-2xl p-5 mb-6 border-amber-500/30">
                        <div className="text-amber-300 font-display font-bold mb-1">Awaiting payment</div>
                        <p className="text-sm text-[var(--text-secondary)]">
                            We&apos;re waiting to receive your transfer. Tracking will start once payment clears.
                        </p>
                    </div>
                )}

                <div className="glass-elevated rounded-2xl p-6 md:p-8 mb-6">
                    <Timeline status={fulfillment} history={order.status_history} canceled={canceled} />
                </div>

                {order.tracking_number && (
                    <div className="glass-elevated rounded-2xl p-5 mb-6" data-testid="tracking-block">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2">Tracking</div>
                        <div className="font-mono text-white text-lg font-bold">{order.tracking_number}</div>
                        {order.tracking_carrier && (
                            <div className="text-xs text-[var(--text-secondary)] mt-1">via {order.tracking_carrier}</div>
                        )}
                    </div>
                )}

                {a.line1 && (
                    <div className="glass rounded-2xl p-5 mb-6" data-testid="shipping-block">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2 inline-flex items-center gap-2">
                            <MapPin size={11} /> Shipping to
                        </div>
                        <div className="text-white font-semibold">{a.full_name}</div>
                        <div className="text-sm text-[var(--text-secondary)]">
                            {a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />
                            {a.suburb} {a.state} {a.postcode}<br />
                            {a.country}{a.phone ? ` · ${a.phone}` : ""}
                        </div>
                    </div>
                )}

                <div className="glass rounded-2xl p-5 mb-6">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-3">Order summary</div>
                    <div className="space-y-1.5">
                        {(order.items || []).map((it, i) => (
                            <div key={i} className="flex justify-between text-sm">
                                <span className="text-white">{it.product_slug}</span>
                                <span className="text-[var(--text-secondary)] font-mono">×{it.quantity}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <OrderDocumentation reference={reference} />
            </div>
        </div>
    );
};

const OrderDocumentation = ({ reference }) => {
    const [coas, setCoas] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/me/orders/${reference}/coas`);
                setCoas(r.data);
            } catch { setCoas([]); }
        })();
    }, [reference]);
    if (!coas || coas.length === 0) return null;
    return (
        <div className="glass rounded-2xl p-5 mt-6" data-testid="order-documentation">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-3 inline-flex items-center gap-2">
                <FileText size={11} /> View Documentation
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-4">
                Manufacturer-supplied Certificates of Analysis for the products in this order.
            </p>
            <ul className="space-y-2">
                {coas.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-black/30 border border-white/5" data-testid={`order-coa-${c.id}`}>
                        <div className="flex items-center gap-2 text-sm">
                            <FileText size={13} className="text-emerald-300" />
                            <span className="text-white">{c.product_name}</span>
                            {c.batch_number && <span className="font-mono text-[var(--gold)] text-xs">· {c.batch_number}</span>}
                        </div>
                        <div className="flex gap-2">
                            <a href={pdfUrl(c.id)} target="_blank" rel="noopener noreferrer" className="text-[var(--gold)] hover:underline text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"><Eye size={11} /> View</a>
                            <a href={pdfUrl(c.id)} download={c.filename} className="text-[var(--text-secondary)] hover:text-white text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"><Download size={11} /> PDF</a>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};
